import { useEffect, useState } from "react";
import { accountApi } from "../../services/accountApi";
import { sitesApi } from "../../services/sitesApi";
import { RoleExplainer } from "./RoleExplainer";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { Field } from "../common/Field";
import { useToast } from "../../context/ToastContext";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { Badge } from "../common/Badge";
import { DataTable } from "../common/DataTable";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { formatDateOnly } from "../../services/hiveActions";
import { Modal } from "../common/Modal";
import { rolesFor, ROLE_LABELS } from "../../services/teamRoles";
import { useFlow } from "../../hooks/useFlow";
import { FLOWS } from "../../services/flows";

/* ADVANCED_ROLES/BASIC_ROLES and the theme switch that chose between
   them now live in services/teamRoles.js, keyed to the account's tier
   rather than the viewer's display preference (#443). */
const STATUS_TONE = { Pending: "warning", Approved: "success", Revoked: "neutral" };
// access_state is computed server-side (V052) so web and mobile cannot
// disagree about what "expired" means. Expired is warning rather than
// neutral: it is a thing the owner may want to act on, whereas a revoked
// member is a decision they already made.
const STATE_TONE = {
  Active: "success", Expired: "warning", Scheduled: "info",
  Pending: "warning", Revoked: "neutral",
};

/**
 * Team Dashboard's Role Management section: invite by email, manage roles
 * on shares you've sent (primary side), accept invites addressed to you
 * (recipient side), and -- for a 'Limited' share -- pick which sites they
 * can access. Backed by /api/account/shares* (api/functions/account.py).
 *
 * Role choices follow the ACCOUNT'S TIER, mirroring the server-side rule
 * in account.py's _assert_role_allowed_for_account. This dropdown is UX
 * convenience; the server enforces it regardless of what gets through.
 *
 * It used to say "theme-gated (Basic theme only offers Admin/Viewer)",
 * and both halves did exactly that -- faithfully, because the server
 * really did gate on the caller's display preference. That was #443 one
 * layer below the navigation: a helper on a Free theme, acting as Admin
 * on a Business account, could not assign an Editor. Both halves moved
 * together; see services/teamRoles.js.
 */
export function RoleManagementSection() {
  const { notify } = useToast();
  const { theme, tier, hasFeature } = useFeatureTheme();
  const { isFree } = useSubscription();
  /* The ACCOUNT's tier, not the viewer's theme (#443). No current role
     is passed here: the INVITE list offers what this account may assign.
     The per-row control below passes the member's own role so it can
     never render blank. */
  const availableRoles = rolesFor(tier);

  const [shares, setShares] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Admin");
  // #187. Collapsed by default: the common case is a permanent member, and
  // two date pickers standing open on every invite make the ordinary path
  // look more complicated than it is.
  const [temporary, setTemporary] = useState(false);
  const [accessFrom, setAccessFrom] = useState("");
  const [accessUntil, setAccessUntil] = useState("");
  const [busy, setBusy] = useState(false);
  /* #379. KEYED TO SOMEBODY HAVING STARTED, because this panel is
     always on screen -- an empty field is not an invite anybody
     began, and treating every visit to the Team page as a started
     invite would bury the real drop-off under browsing.

     An invite abandoned is somebody deciding not to share their
     apiary after all, which is the most interesting drop-off in the
     product -- it is the moment an account stops being one person. */
  const inviteFlow = useFlow(FLOWS.INVITE, inviteEmail.trim().length > 0);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [sitesShare, setSitesShare] = useState(null);
  const [allSites, setAllSites] = useState(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState(new Set());

  /*
   * WHICH SITES a Limited Editor can reach, chosen WITH the invite.
   *
   * The role existed and the mechanism behind it was complete --
   * app.AccountShareSites, enforced on every hive read and write -- but
   * the sites could only be assigned afterwards, from the member list.
   * So the obvious path was to invite somebody and stop.
   *
   * That is worse than an unfinished feature. Read the restriction
   * clause in hives.py: a Limited share with NO site rows matches
   * nothing, so the member signs in and sees an EMPTY APP. They conclude
   * it is broken, and the person who invited them -- usually asking a
   * favour -- looks like they sent something broken.
   *
   * The invite already creates the share row as Pending and returns its
   * id, so the sites can be set in the same breath. No new endpoint.
   */
  const [explaining, setExplaining] = useState(null);
  const [inviteSites, setInviteSites] = useState([]);

  useEffect(() => {
    // Populates the SAME allSites the assign-sites dialog uses, rather
    // than a second copy -- two lists of one thing is how they end up
    // disagreeing. Best-effort: the picker is needed for one role only,
    // and a failure here must not stop somebody inviting an Admin.
    sitesApi.list().then((r) => setAllSites(r.sites || [])).catch(() => setAllSites([]));
  }, []);

  async function load() {
    const r = await accountApi.listShares();
    setShares(r);
  }
  useEffect(() => { load(); }, []);

  async function sendInvite() {
    if (!inviteEmail.trim()) return notify("Enter an email address", "warning");
    // Refused rather than sent empty: a Limited member with no sites sees
    // nothing at all, so this would invite somebody into a blank app.
    if (inviteRole === "Limited" && inviteSites.length === 0) {
      return notify("Pick which sites they can work at — a Limited Editor with none sees nothing.", "warning");
    }
    setBusy(true);
    try {
      const created = await accountApi.inviteShare(
        inviteEmail.trim(),
        inviteRole,
        temporary ? accessFrom : undefined,
        temporary ? accessUntil : undefined,
      );
      if (inviteRole === "Limited" && created?.share_id) {
        // Separately, and deliberately not folded into the invite call:
        // if this fails the invite still stands and the sites can be set
        // from the member list. Saying so beats a silent half-invite.
        try {
          await accountApi.setShareSites(created.share_id, inviteSites);
        } catch {
          notify(
            "Invite sent, but the sites did not save — set them on the member below before they sign in.",
            "warning",
          );
        }
      }
      inviteFlow.finished();
      notify(`Invite sent to ${inviteEmail.trim()}`, "success");
      setInviteEmail("");
      setInviteSites([]);
      setTemporary(false);
      setAccessFrom("");
      setAccessUntil("");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not send invite", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(shareId, role) {
    try {
      await accountApi.updateShareRole(shareId, role);
      notify("Role updated", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not update role", "danger");
    }
  }

  async function changeMoneyAccess(share, canSee) {
    try {
      // Sends the role unchanged alongside it: the endpoint owns both
      // fields on the row, and omitting the role would be a partial PUT.
      await accountApi.updateShareRole(share.share_id, share.role, canSee);
      notify(canSee ? "They can see money again" : "Money is now hidden from them", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not change that", "danger");
    }
  }

  async function bringBack(share) {
    try {
      const r = await accountApi.reactivateShare(share.share_id);
      notify(
        r.expiry_cleared
          ? `${r.shared_email} is back, with no end date this time`
          : `${r.shared_email} is back as ${ROLE_LABELS[r.role] || r.role}`,
        "success",
      );
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not bring them back", "danger");
    }
  }

  async function acceptInvite(shareId) {
    try {
      await accountApi.approveShare(shareId);
      notify("Invite accepted", "success");
      // Explained at the moment of accepting (#308). Most people have never
      // looked at the team features the first time somebody adds them to an
      // account, so the role name on its own tells them nothing -- and
      // "Limited Editor (site-specific)" sounds like a restriction without
      // saying which.
      const accepted = (shares?.received || []).find((r) => r.share_id === shareId);
      if (accepted) setExplaining(accepted);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not accept invite", "danger");
    }
  }

  async function confirmRevoke() {
    setBusy(true);
    try {
      await accountApi.revokeShare(pendingRevoke.share_id);
      notify("Access revoked", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not revoke access", "danger");
    } finally {
      setBusy(false);
      setPendingRevoke(null);
    }
  }

  async function openSites(share) {
    setSitesShare(share);
    const [sitesRes, shareSitesRes] = await Promise.all([sitesApi.list(), accountApi.listShareSites(share.share_id)]);
    setAllSites(sitesRes.sites);
    setSelectedSiteIds(new Set(shareSitesRes.site_ids));
  }

  function toggleSite(siteId) {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  async function saveSites() {
    try {
      await accountApi.setShareSites(sitesShare.share_id, [...selectedSiteIds]);
      notify("Site access updated", "success");
      setSitesShare(null);
    } catch (err) {
      notify(err.apiMessage || "Could not update site access", "danger");
    }
  }

  return (
    <div>
      {explaining ? (
        <RoleExplainer share={explaining} onClose={() => setExplaining(null)} />
      ) : null}

      <Card title="Invite a Team Member" className="section-spacing">
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ flex: 1, minWidth: 220 }}>
            Email
            <input id="role-management-section-invite-email" name="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="touch-target"
              style={{ width: "100%" }}
            />
          </label>
          <label>
            Role
            <select id="role-management-section-invite-role" name="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="touch-target" style={{ display: "block" }}>
              {availableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </label>
          <Button onClick={sendInvite} disabled={busy}>Send Invite</Button>
        </div>

        {/* Chosen WITH the invite, not after it. The role names sites, so
            the invite has to ask about them -- and a Limited share with
            none matches nothing, which means the member signs in to an
            empty app and thinks it is broken. */}
        {inviteRole === "Limited" && (
          <div style={{ marginTop: "var(--space-3)" }}>
            <Field
              label="Which sites can they work at?"
              hint={
                inviteSites.length
                  ? `${inviteSites.length} of ${allSites.length} selected. A Limited Editor sees only these.`
                  : "A Limited Editor sees only these. Without at least one they see nothing at all."
              }
            >
              {(id, aria) => (
                /* Columns and a scroll ceiling, not a wrapping row. A
                   handful of sites reads fine either way, but an account
                   with twenty turns a flex-wrap row into a wall of
                   checkboxes that pushes the Send button off the screen.
                   auto-fill gives 2-4 columns by width on its own, which is
                   the same thing SiteGrid and the summary cards do, and the
                   max-height only bites once the list is genuinely long --
                   a small account still sees no scrollbar. The count is in
                   the hint above because once this scrolls you can no
                   longer see everything you have ticked. */
                <div
                  id={id}
                  {...aria}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                    gap: "var(--space-2)",
                    maxHeight: "13rem",
                    overflowY: "auto",
                    padding: allSites.length > 6 ? "var(--space-2)" : 0,
                    border: allSites.length > 6 ? "1px solid var(--color-border)" : "none",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  {(allSites || []).length === 0 ? (
                    <span style={{ color: "var(--color-text-muted)" }}>
                      You have no sites yet — add one before inviting a site-specific helper.
                    </span>
                  ) : (allSites || []).map((site) => (
                    /* The touch target is the LABEL, not the box. It carried
                       .touch-target originally, which forces a 48px minimum
                       -- right for buttons and text inputs, wrong here: it
                       drew a big square with sharp corners, unlike every
                       other checkbox in the app, which are browser default
                       and rounded. Sizing the label keeps the whole row
                       comfortably tappable with gloves on, which was the
                       point of the class, without restyling the control. */
                    <label
                      key={site.site_id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        minHeight: "var(--touch-target-min)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={inviteSites.includes(site.site_id)}
                        onChange={(e) =>
                          setInviteSites((prev) =>
                            e.target.checked
                              ? [...prev, site.site_id]
                              : prev.filter((id2) => id2 !== site.site_id))
                        }
                      />
                      {site.dock_name}
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </div>
        )}

        {/* Only offered on a paid tier (#186, #187): a seasonal harvest
            crew is what a sideliner pays for, while the common free case
            is a spouse or one steady helper -- a permanent relationship.
            Hidden rather than shown-and-refused, because the API returns a
            tier error and collecting two dates before saying no is worse
            than not offering them. */}
        {!isFree && (
          <div style={{ marginTop: "var(--space-3)" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <input id="role-management-section-temporary" name="temporary" type="checkbox" checked={temporary} onChange={(e) => setTemporary(e.target.checked)} />
              Just for a season
            </label>
            {temporary && (
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginTop: "var(--space-2)" }}>
                <Field label="From" hint="Leave blank to start now.">
                  {(id, aria) => <input id={id} {...aria} type="date" value={accessFrom}
                    onChange={(e) => setAccessFrom(e.target.value)} className="touch-target" />}
                </Field>
                <Field label="Until" hint="Their last day. Access ends after it.">
                  {(id, aria) => <input id={id} {...aria} type="date" value={accessUntil}
                    onChange={(e) => setAccessUntil(e.target.value)} className="touch-target" />}
                </Field>
              </div>
            )}
          </div>
        )}
        {/* THE ADVICE HAD TO CHANGE WITH THE RULE (#443). This said
            "Switch to Advanced (or Custom)", which was correct while the
            gate read the viewer's theme and is now advice that does
            nothing -- the worst kind, because following it and seeing no
            change reads as the app being broken. The tier is what opens
            these roles, so the tier is what it names. */}
        {tier === "Free" && (
          <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", marginTop: "var(--space-2)" }}>
            A free account can share with Admin and Viewer roles. Editor and the
            site-specific Limited Editor role are part of a paid plan.
          </p>
        )}
      </Card>

      <Card title="Your Team (people you've shared access with)" className="section-spacing">
        {/* THE LIMIT, BEFORE THE REFUSAL (#455). Free gets 2 permanent
            shared people, Harvester 10, Business unlimited -- documented
            in PRICING_TIERS.md, configured, and enforced on the invite
            path all along. Reported as "there's nothing indicating a
            limit to the 'Your Team' members on the free tier", which is
            exactly right: it was found by hitting it.

            PENDING COUNTS, because the server counts it: an invitation
            sent is a seat promised, so that the refusal lands on the
            owner who sent it rather than on the invitee who clicked a
            link. The count comes from the API for that reason -- a
            number computed here from shares.sent could disagree with the
            one the gate uses.

            Not an upsell. #387 settled that Free should SHOW the paid
            features rather than hide them, but a seat count is
            information; the upgrade belongs at the point of refusal,
            where it already is. */}
        {shares?.seats && (
          <p style={{ marginTop: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            {shares.seats.limit == null
              ? `${shares.seats.used} on your team — your plan has no limit.`
              : `${shares.seats.used} of ${shares.seats.limit} team ${shares.seats.limit === 1 ? "seat" : "seats"} used.`}
            {shares.seats.limit != null && shares.seats.used > 0
              ? " Invitations that have not been accepted yet count too."
              : ""}
          </p>
        )}
        <DataTable
          rows={shares?.sent}
          loading={!shares}
          rowKey="share_id"
          emptyTitle="You haven't shared your account with anyone yet"
          columns={[
            { key: "shared_email", header: "Email" },
            {
              key: "role",
              header: "Role",
              render: (r) => (
                <select id="role-management-section-role" name="role"
                  value={r.role}
                  disabled={r.status === "Revoked"}
                  onChange={(e) => changeRole(r.share_id, e.target.value)}
                  className="touch-target"
                >
                  {/* The member's OWN role is passed, so a role this
                      account can no longer assign still renders instead
                      of leaving the select blank (#443). */}
                  {rolesFor(tier, r.role).map((role) => (
                    <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>
                  ))}
                </select>
              ),
            },
            // #235. Business tier only, because that is where money is
            // worth hiding: a Free or Harvester account has no vendor
            // ledger for a helper to see. Rendered as a plain checkbox
            // rather than a second role dropdown -- it is one yes/no, and
            // making it look like a role invites the functional-role
            // matrix this task exists to avoid.
            ...(hasFeature("HoneyBusiness")
              ? [{
                  key: "can_see_financials",
                  header: "Sees money",
                  render: (r) => (
                    <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                      <input
                        type="checkbox"
                        checked={r.can_see_financials !== 0 && r.can_see_financials !== false}
                        disabled={r.status === "Revoked"}
                        onChange={(e) => changeMoneyAccess(r, e.target.checked)}
                        aria-label={`${r.shared_email} can see money`}
                      />
                      <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                        {r.can_see_financials !== 0 && r.can_see_financials !== false ? "Yes" : "Hidden"}
                      </span>
                    </label>
                  ),
                }]
              : []),
            {
              key: "access_state",
              header: "Status",
              render: (r) => (
                <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
                  <Badge tone={STATE_TONE[r.access_state] || STATUS_TONE[r.status]}>
                    {r.access_state || r.status}
                  </Badge>
                  {/* The date, not just the word. "Expired" alone sends the
                      owner hunting for when and why. */}
                  {r.access_until && (
                    <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                      {r.access_state === "Expired" ? "ended " : "until "}
                      {formatDateOnly(r.access_until)}
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  {r.role === "Limited" && r.status !== "Revoked" && (
                    <Button size="sm" variant="secondary" onClick={() => openSites(r)}>Manage Sites</Button>
                  )}
                  {r.status !== "Revoked" && <Button size="sm" variant="danger" onClick={() => setPendingRevoke(r)}>Revoke</Button>}
                  {/* #188. Offered for a revoked member AND an expired one,
                      because both are "they are not here and I want them
                      back". Their role and site assignments were never
                      deleted -- removal has always been a soft revoke. */}
                  {(r.status === "Revoked" || r.access_state === "Expired") && (
                    <Button size="sm" variant="secondary" onClick={() => bringBack(r)}>Bring Back</Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      <Card title="Accounts Shared With You">
        <DataTable
          rows={shares?.received}
          loading={!shares}
          rowKey="share_id"
          emptyTitle="No one has shared their account with you"
          columns={[
            { key: "primary_email", header: "Account Owner" },
            { key: "role", header: "Your Role", render: (r) => ROLE_LABELS[r.role] || r.role },
            { key: "status", header: "Status", render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
            {
              key: "actions",
              header: "",
              render: (r) => r.status === "Pending" && <Button size="sm" onClick={() => acceptInvite(r.share_id)}>Accept</Button>,
            },
          ]}
        />
      </Card>

      <ConfirmDialog
        open={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke team access?"
        confirmLabel="Revoke"
        danger
        busy={busy}
      >
        <p>{pendingRevoke?.shared_email} will immediately lose access to your account.</p>
      </ConfirmDialog>

      <Modal
        open={!!sitesShare}
        onClose={() => setSitesShare(null)}
        title={`Site access for ${sitesShare?.shared_email || ""}`}
        footer={<Button onClick={saveSites}>Save</Button>}
      >
        {!allSites ? (
          <p>Loading sites…</p>
        ) : allSites.length === 0 ? (
          <p>No sites yet -- add a site before restricting access to it.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-2)" }}>
            {allSites.map((s) => (
              <label key={s.site_id} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <input type="checkbox" checked={selectedSiteIds.has(s.site_id)} onChange={() => toggleSite(s.site_id)} />
                {s.dock_name}
              </label>
            ))}
          </div>
        )}
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", marginTop: "var(--space-2)" }}>
          Unchecked sites (and their hives) will be invisible to this team member. Leaving everything unchecked restricts them to nothing.
        </p>
      </Modal>
    </div>
  );
}
